import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable'
import { addFooter, addHeader, jsPDFCustom } from 'src/app/interfaces/JsPDF.extended.interface';

applyPlugin(jsPDF)

/**
 * Generates a PDF report for the Farm Woodlot Tool
 * @param {Object} data - The report data
 * @param {string} data.reportTargetName - Name of the report target
 * @param {Date} data.todayDate - Current date
 * @param {Object} data.stageData - Data for each stage (1-6)
 * @returns {jsPDF} The generated PDF document
 */
export function generateFarmWoodlotPDF(data) {
  const { reportTargetName, todayDate, stageData } = data;

  const doc: jsPDFCustom = new jsPDF('p', 'mm', 'a4') as jsPDFCustom ;
  const headerHeight = 30;
  const title = "Farm Woodlot Report"
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentTopMargin: number = 50;
  let currentY = contentTopMargin;

  addHeader(title, doc, margin, headerHeight)
  addFooter(doc, margin);

  // Helper function to check if stage has meaningful data
  const hasStageData = (stageNumber) => {
    const stage = stageData[stageNumber];
    if (!stage || !stage.data) return false;

    // Check if the data object has any meaningful values
    const data = stage.data;
    const values = Object.values(data);

    // Filter out empty strings, null, undefined, and zero values that might be defaults
    const meaningfulValues = values.filter(value =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(typeof value === 'number' && value === 0) &&
      !(typeof value === 'string' && value.trim() === '')
    );

    return meaningfulValues.length > 0;
  };

  // Helper function to add new page if needed
  const checkPageBreak = (requiredHeight) => {
    if (currentY + requiredHeight > pageHeight - margin) {
      doc.addPage();
      addHeader(title, doc, margin, headerHeight)
      addFooter(doc, margin);
      currentY = contentTopMargin;
    }
  };

  // Helper function to add wrapped text
  const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * fontSize * 0.3527777778); // Convert pt to mm
  };

  // Helper function to remove zero characters from the given text
  const removeZeroSpacesChars = (str: string) => {
    return str.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
  }

  // Header
  // doc.setFontSize(18);
  // doc.setFont('helvetica', 'bold');
  // doc.text('Farm Woodlot Tool', margin, currentY);
  // currentY += 10;
  //
  // doc.setFontSize(12);
  // doc.setFont('helvetica', 'normal');
  // doc.text(`Date: ${todayDate.toDateString()}`, margin, currentY);
  // currentY += 8;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(reportTargetName, margin, currentY);
  currentY += 15;

  // Stage 1: Species and Planting Materials
  if (stageData[1]?.completed && hasStageData(1)) {
    checkPageBreak(95);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Species and Planting Materials required', margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // Introduction paragraphs
    const intro1 = 'This form provides guidance on the types of tree species you can plant for different end-uses, materials likely required at the time of establishment, and advice on sourcing seedlings and planting labour. Note that you will likely have to order seedlings up to a year in advance, so good planning is essential.';
    currentY = addWrappedText(intro1, margin, currentY, pageWidth - 2 * margin);
    currentY += 5;

    const intro2 = 'For any timber production enterprise, genetics matter so be sure to discuss genetics options with your supplier prior to purchase.';
    currentY = addWrappedText(intro2, margin, currentY, pageWidth - 2 * margin);
    currentY += 10;

    // Primary/Secondary use info
    const stage1Data = stageData[1].data;

    if (stage1Data.primaryUse) {
      doc.setFont('helvetica', 'bold');
      doc.text('Primary Use: ', margin, currentY);
      doc.setFont('helvetica', 'normal');
      doc.text(stage1Data.primaryUse, margin + 25, currentY);
      currentY += 6;
    }

    if (stage1Data.secondaryUse) {
      doc.setFont('helvetica', 'bold');
      doc.text('Secondary Use: ', margin, currentY);
      doc.setFont('helvetica', 'normal');
      doc.text(stage1Data.secondaryUse, margin + 30, currentY);
      currentY += 6;
    }

    if (stage1Data.recommendedSpecies) {
      doc.setFont('helvetica', 'bold');
      doc.text('Recommended species: ', margin, currentY);
      doc.setFont('helvetica', 'normal');
      currentY = addWrappedText(stage1Data.recommendedSpecies, margin + 40, currentY, pageWidth - margin - 40);
      currentY += 8;
    }

    // Species Description
    if (stage1Data.speciesDescription) {
      doc.setFont('helvetica', 'bold');
      doc.text('Species Description:', margin, currentY);
      currentY += 5;
      doc.setFont('helvetica', 'normal');
      currentY = addWrappedText(stage1Data.speciesDescription, margin, currentY, pageWidth - 2 * margin);
      currentY += 8;
    }

    if (stage1Data.urlInfo) {
      doc.setFont('helvetica', 'bold');
      doc.text('More info:', margin, currentY);
      currentY += 5;
      doc.setFont('helvetica', 'normal');
      currentY = addWrappedText(stage1Data.urlInfo, margin, currentY, pageWidth - 2 * margin);
      currentY += 10;
    }

    // Planting layout table - only if we have the data
    if (stage1Data.recommendedPlantingLayout || stage1Data.alongRowSpacing || stage1Data.betweenRowSpacing) {
      checkPageBreak(40);
      const plantingLayoutData = [];

      if (stage1Data.recommendedPlantingLayout) {
        plantingLayoutData.push(['Recommended planting layout', stage1Data.recommendedPlantingLayout]);
      }
      if (stage1Data.alongRowSpacing) {
        plantingLayoutData.push(['Along row spacing:', stage1Data.alongRowSpacing]);
      }
      if (stage1Data.betweenRowSpacing) {
        plantingLayoutData.push(['Between row spacing:', stage1Data.betweenRowSpacing]);
      }

      if (plantingLayoutData.length > 0) {
        doc.autoTable({
          startY: currentY,
          head: [['Property', 'Value']],
          body: plantingLayoutData,
          theme: 'grid',
          headStyles: { fillColor: [200, 200, 200] },
          margin: { top: contentTopMargin, left: margin, right: margin },
          styles: { fontSize: 10 },
          didDrawPage: (data) => {
            addHeader(title, doc, margin, headerHeight)
            addFooter(doc, margin);
          }
        });

        currentY = doc.lastAutoTable.finalY + 10;
      }
    }

    // Calculations table - only if we have calculation data
    const calculationFields = [
      { key: 'stemsPerHectare', label: 'Stems per hectare (SPH)' },
      { key: 'numberSeedingsRequired', label: 'Number seedlings required' },
      { key: 'numberTreeStocksRequired', label: 'Number tree socks required' },
      { key: 'amountFertiliserRequired', label: 'Amount of fertiliser required (kg)' }
    ];

    const calculationData = calculationFields
      .filter(field => stage1Data[field.key] !== undefined && stage1Data[field.key] !== null && stage1Data[field.key] !== 0)
      .map(field => [field.label, stage1Data[field.key].toString()]);

    if (calculationData.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Calculations:', margin, currentY);
      currentY += 8;

      doc.autoTable({
        startY: currentY,
        head: [['Calculation', 'Value']],
        body: calculationData,
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200] },
        margin: { top: contentTopMargin, left: margin, right: margin },
        styles: { fontSize: 10 },
        didDrawPage: (data) => {
          addHeader(title, doc, margin, headerHeight)
          addFooter(doc, margin);
        }
      });

      currentY = doc.lastAutoTable.finalY + 8;

      // Fertiliser note - only show if fertiliser amount is present
      if (stage1Data.amountFertiliserRequired && stage1Data.amountFertiliserRequired > 0) {
        const fertNote = 'Fertiliser amount is based on an assumed 125g DAP (Di Ammonium Phosphate) per seedling. Please note that although fertiliser application at planting is a standard practice in the forestry industry, it is optional, and you should seek further advice as to whether this is required or not for your site. Generally, ex-pasture sites do not require fertiliser application.';
        currentY = addWrappedText(fertNote, margin, currentY, pageWidth - 2 * margin, 9);
      }
    }

    currentY += 15;
  }

  // Stage 2: Fencing Materials
  if (stageData[2]?.completed && hasStageData(2)) {
    checkPageBreak(50);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Fencing materials required', margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const fenceIntro1 = 'Fencing is likely required to protect young trees from vermin and stock alike. This form provides an estimate of fencing materials likely required to protect your trees.';
    currentY = addWrappedText(fenceIntro1, margin, currentY, pageWidth - 2 * margin);
    currentY += 5;

    const fenceIntro2 = 'In addition to fencing, browsing control can also be an effective means of protecting trees. For more information on browsing control, please refer to this link:';
    currentY = addWrappedText(fenceIntro2, margin, currentY, pageWidth - 2 * margin);
    currentY += 10;

    const stage2Data = stageData[2].data;

    if (stage2Data.selectedFencingType) {
      doc.text(`Choose the type of fence you required: ${stage2Data.selectedFencingType}`, margin, currentY);
      currentY += 6;
    }

    if (stage2Data.lengthOfFenceRequired) {
      doc.text(`Length of Fence Required (m): ${stage2Data.lengthOfFenceRequired}`, margin, currentY);
      currentY += 12;
    }

    // Post Type table - only if we have post data
    if ((stage2Data.star && stage2Data.star > 0) || (stage2Data.steelWood && stage2Data.steelWood > 0)) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Post Type:', margin, currentY);
      currentY += 8;

      doc.autoTable({
        startY: currentY,
        head: [['Star', 'Steel/Wood']],
        body: [[
          (stage2Data.star || 0).toString(),
          (stage2Data.steelWood || 0).toString()
        ]],
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200] },
        margin: { top: contentTopMargin, left: margin, right: margin },
        styles: { fontSize: 10 },
        didDrawPage: (data) => {
          addHeader(title, doc, margin, headerHeight)
          addFooter(doc, margin);
        }
      });

      currentY = doc.lastAutoTable.finalY + 10;
    }

    // Wire Type table - only if we have wire data
    if ((stage2Data.standardWire && stage2Data.standardWire > 0) ||
        (stage2Data.barbWire && stage2Data.barbWire > 0) ||
        (stage2Data.wallabyProofMesh && stage2Data.wallabyProofMesh > 0)) {

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Wire Type:', margin, currentY);
      currentY += 8;

      doc.autoTable({
        startY: currentY,
        head: [['Standard Wire', 'Barb Wire', 'Wallaby Proof Mesh']],
        body: [[
          (stage2Data.standardWire || 0).toString(),
          (stage2Data.barbWire || 0).toString(),
          (stage2Data.wallabyProofMesh || 0).toString()
        ]],
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200] },
        margin: { top: contentTopMargin, left: margin, right: margin },
        styles: { fontSize: 10 },
        didDrawPage: (data) => {
          addHeader(title, doc, margin, headerHeight)
          addFooter(doc, margin);
        }
      });

      currentY = doc.lastAutoTable.finalY + 15;
    }
  }

  // Stage 3: Establishment and Weed Control
  if (stageData[3]?.completed && hasStageData(3)) {
    checkPageBreak(80);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Establishment and Weed Control guidelines', margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const weedIntro = 'Preparation of the ground for any crop (including trees) is essential for a successful crop and to protect the long-term sustainability of the soil. Good preparation will:';
    currentY = addWrappedText(weedIntro, margin, currentY, pageWidth - 2 * margin);
    currentY += 8;

    // Numbered list
    const preparationPoints = [
      'Minimise soil disturbance;',
      'Result in good seedling survival;',
      'Allow the crop to grow as uniformly as possible;',
      'Increase the effectiveness of weed control operations and minimise the use of chemicals;',
      'Mitigate against frost damage, waterlogging or drought;',
      'Reduce risk of wind damage later in the rotation;',
      'Reduce and cater for fire risk; and',
      'Provide good access for thinning and pruning.'
    ];

    preparationPoints.forEach((point, index) => {
      checkPageBreak(8);
      doc.text(`${index + 1}. ${point}`, margin + 5, currentY);
      currentY += 5;
    });

    currentY += 5;

    // Additional paragraphs
    const addText1 = 'In addition to ground preparation, completely removing difficult to control or highly competitive weeds prior to tree planting, allows the planted trees to grow as quickly as possible so they can occupy the site';
    currentY = addWrappedText(addText1, margin, currentY, pageWidth - 2 * margin);
    currentY += 8;

    const addText2 = 'Weeds are a problem because they can compete with trees for water, nutrients and light. By their nature, most weed species are aggressive at occupying a site. If additional nutrients are applied using fertilisers, or water is applied by irrigation, weeds will generally benefit from these much more than the trees. Weeds can also contribute to increased frost damage, browsing susceptibility, fungal diseases and insect attack in young tree seedlings.';
    currentY = addWrappedText(addText2, margin, currentY, pageWidth - 2 * margin);
    currentY += 12;

    // More information section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('More information', margin, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 255); // Blue for links
    doc.text('• Site Preparation Guidelines', margin + 5, currentY);
    currentY += 5;
    doc.text('• Weed Control Guidelines', margin + 5, currentY);
    currentY += 5;
    doc.text('• Tree Farming Guide', margin + 5, currentY);
    currentY += 15;
    doc.setTextColor(0, 0, 0); // Reset to black
  }

  // Stage 4: Yield Estimations
  if (stageData[4]?.completed && hasStageData(4)) {
    checkPageBreak(60);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Estimations of yields for final harvest of trees', margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const yieldIntro1 = 'The Enterprise Suitability ratings presented by the Crop Decision Tool can be used as a rough guide as to the likely growth rates your trees will achieve if planted on these sites. The tool below can be used to assign likely growth rates to the area under each rating, from which estimated volumes at harvest age can be derived. The Estimated Mean Annual Increment figures provided as defaults are indicative only, and you should seek advice from a professional as to the likely growth rates achieveable on your site.';
    currentY = addWrappedText(yieldIntro1, margin, currentY, pageWidth - 2 * margin);
    currentY += 8;

    const yieldIntro2 = 'Note that pine species are generally thinned in Tasmania, either once or twice, to achieve a higher quality product at final harvest.';
    currentY = addWrappedText(yieldIntro2, margin, currentY, pageWidth - 2 * margin);
    currentY += 10;

    const stage4Data = stageData[4].data;

    // Build yield calculations table with only fields that have data
    const yieldFields = [
      { key: 'selectedCropSpecies', label: 'Selected Crop Species', type: 'string' },
      { key: 'mappedWellSuited', label: 'Mapped area (ha) WellSuited', type: 'number' },
      { key: 'mappedSuitable', label: 'Mapped area (ha) Suitable', type: 'number' },
      { key: 'mappedModeratelySuitable', label: 'Mapped area (ha) Moderately Suitable', type: 'number' },
      { key: 'mappedUnsuitable', label: 'Mapped area (ha) Unsuitable', type: 'number' },
      { key: 'estimatedWellSuited', label: 'Estimated Mean Annual Increment (t/ha/yr) Well Suited', type: 'number' },
      { key: 'estimatedSuitable', label: 'Estimated Mean Annual Increment (t/ha/yr) Suitable', type: 'number' },
      { key: 'estimatedModeratelySuitable', label: 'Estimated Mean Annual Increment (t/ha/yr) Moderately Suitable', type: 'number' },
      { key: 'estimatedUnsuitable', label: 'Estimated Mean Annual Increment (t/ha/yr) Unsuitable', type: 'number' },
      { key: 'totalArea', label: 'Total Area', type: 'number' },
      { key: 'averageMAI', label: 'Average MAI', type: 'number' },
      { key: 'harvestableVolume', label: 'Harvestable Volume', type: 'number' },
      { key: 'harvestableVolumePerHectare', label: 'Harvestable Volume Per Hectare', type: 'number' }
    ];

    const yieldTableData = yieldFields
      .filter(field => {
        const value = stage4Data[field.key];
        if (field.type === 'string') {
          return value && value.trim() !== '';
        } else {
          return value !== undefined && value !== null && value !== 0;
        }
      })
      .map(field => [
        field.label,
        field.type === 'string' ? stage4Data[field.key] : stage4Data[field.key].toString()
      ]);

    if (yieldTableData.length > 0) {
      doc.autoTable({
        startY: currentY,
        head: [['Property', 'Value']],
        body: yieldTableData,
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200] },
        margin: { top: contentTopMargin, left: margin, right: margin },
        styles: { fontSize: 9 },
        didDrawPage: (data) => {
          addHeader(title, doc, margin, headerHeight)
          addFooter(doc, margin);
        }
      });

      currentY = doc.lastAutoTable.finalY + 15;
    }
  }

  // Stage 5: Market Guidelines
  if (stageData[5]?.completed && hasStageData(5)) {
    checkPageBreak(40);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Market Guideline', margin, currentY);
    currentY += 10;

    const stage5Data = stageData[5].data;

    // Build market data table with only fields that have data
    const marketFields = [
      { key: 'selectedMarketType', label: 'Market Type' },
      { key: 'selectedTopic', label: 'Topic' },
      { key: 'overviewDescription', label: 'Overview' },
      { key: 'overviewURL', label: 'Link' }
    ];

    const marketTableData = marketFields
      .filter(field => stage5Data[field.key] && stage5Data[field.key].trim() !== '')
      .map(field => [field.label, removeZeroSpacesChars(stage5Data[field.key])]);

    if (marketTableData.length > 0) {
      doc.autoTable({
        startY: currentY,
        head: [['Property', 'Value']],
        body: marketTableData,
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200] },
        margin: { top: contentTopMargin, left: margin, right: margin },
        styles: { fontSize: 10 },
        didDrawPage: (data) => {
          addHeader(title, doc, margin, headerHeight)
          addFooter(doc, margin);
        }
      });

      currentY = doc.lastAutoTable.finalY + 15;
    }
  }

  // Stage 6: PTR Guidelines
  if (stageData[6]?.completed && hasStageData(6)) {
    checkPageBreak(80);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PTR Guideline', margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const ptrIntro1 = 'In Tasmania private timber reserves are an integral part of the State\'s commitment to encouraging and fostering the sustainable management of private forests. Forestry, as a major statewide industry, is covered by a statewide planning system in the form of the Forest Practices Act 1985 and Forest Practices Code. Private timber reserves create a secure environment for investment in private forestry within the State and help to deliver commitments for the sustainable management of private forests.';
    currentY = addWrappedText(ptrIntro1, margin, currentY, pageWidth - 2 * margin);
    currentY += 8;

    const ptrIntro2 = 'A private timber reserve is an area of private land set aside for forestry purposes and registered on the title. It is land or forest used to establish forests, or grow or harvest timber in accordance with the Forest Practices Code.\nA private timber reserve is a parcel of private land or forest of at least five hectares in area set aside to grow or harvest forests. It may be an area of:';
    currentY = addWrappedText(ptrIntro2, margin, currentY, pageWidth - 2 * margin);
    currentY += 8;

    // PTR list
    doc.text('• native forest; or', margin + 5, currentY);
    currentY += 5;
    doc.text('• a tree plantation; or', margin + 5, currentY);
    currentY += 5;
    doc.text('• an area of land intended to be planted in the near future.', margin + 5, currentY);
    currentY += 10;

    const ptrNote = 'On land declared a private timber reserve local government approval is not required when undertaking forestry operations. Under the Land Use Planning and Approvals Act 1993 section 20 (7), nothing in any planning scheme, or interim order, affects the management of land declared as a private timber reserve under the Forest Practices Act 1985';
    currentY = addWrappedText(ptrNote, margin, currentY, pageWidth - 2 * margin);
    currentY += 12;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('More information', margin, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 255);
    doc.text('• Private Timber Reserves', margin + 5, currentY);
    doc.setTextColor(0, 0, 0);
    currentY += 15;
  }

  // Only add disclaimers page if we have any content rendered
  const hasAnyContent = [1, 2, 3, 4, 5, 6].some(stage =>
    stageData[stage]?.completed && hasStageData(stage)
  );

  if (hasAnyContent) {
    // Add disclaimers on new page
    doc.addPage();
    addHeader(title, doc, margin, headerHeight)
    addFooter(doc, margin);
    currentY = contentTopMargin;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('General Disclaimer for Farm & Forest Mapper', margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const disclaimer1 = 'The Farm and Forest Mapper is subject to the terms and conditions as detailed in the conditions of use below. Both Private Forests Tasmania and the Forest Practices Authority exclude all liability to any person arising directly or indirectly from using this site and any information or material available from it.';
    currentY = addWrappedText(disclaimer1, margin, currentY, pageWidth - 2 * margin);
    currentY += 10;

    //const addHeaderAndFooter = (doc) => {
  }

  return doc;
}
